import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const MedicalBillingOutsourcing = () => {
  const currentDate = new Date().toISOString();

  // Schema Markup
  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Medical Billing Outsourcing Services",
    "description": "Professional medical billing outsourcing services for healthcare providers. Reduce costs by 30-50%, improve claim accuracy, and accelerate reimbursements with expert billing specialists.",
    "provider": {
      "@type": "MedicalBusiness",
      "name": "MDeRCM",
      "url": "https://mdercm.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Five Greentree Centre, 525 Route 73 North, Suite 104",
        "addressLocality": "Marlton",
        "addressRegion": "NJ",
        "postalCode": "08053",
        "addressCountry": "US"
      }
    },
    "areaServed": "United States",
    "offers": {
      "@type": "Offer",
      "price": "Contact for pricing",
      "priceCurrency": "USD"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is medical billing outsourcing?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Medical billing outsourcing is the practice of hiring external specialists to handle your practice's billing, coding, claims submission, and revenue collection processes. This allows healthcare providers to focus on patient care while experts manage the complex billing operations."
        }
      },
      {
        "@type": "Question",
        "name": "How much can I save with medical billing outsourcing services?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Healthcare providers typically save 30-50% on operational costs through medical billing outsourcing. Additionally, you'll see improved collection rates, reduced claim denials, and faster reimbursements."
        }
      },
      {
        "@type": "Question",
        "name": "Is medical billing outsourcing secure and HIPAA compliant?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, reputable medical billing outsourcing companies like MDeRCM are fully HIPAA compliant with ISO certifications, encrypted data transmission, regular security audits, and strict access controls to protect patient information."
        }
      }
    ]
  };

  const benefits = [
    {
      icon: '💰',
      title: '30-50% Cost Reduction',
      description: 'Eliminate overhead costs of in-house billing staff, software, and infrastructure while improving efficiency.'
    },
    {
      icon: '📈',
      title: '95%+ Collection Rate',
      description: 'Expert billing specialists ensure maximum reimbursement with accurate coding and persistent follow-up.'
    },
    {
      icon: '⚡',
      title: 'Faster Reimbursements',
      description: 'Reduce payment cycles from 60 days to 15-20 days with streamlined claim submission and follow-up.'
    },
    {
      icon: '✅',
      title: '98% First-Pass Rate',
      description: 'AI-powered validation and expert coding minimize claim denials and rejections from payers.'
    },
    {
      icon: '👥',
      title: 'Dedicated Team',
      description: 'Access certified medical billing specialists and coders without recruitment and training costs.'
    },
    {
      icon: '🔒',
      title: 'HIPAA Compliance',
      description: 'Full compliance with healthcare regulations, ISO certifications, and data security standards.'
    }
  ];

  const services = [
    { name: 'Claims Submission & Management', icon: '📄' },
    { name: 'Medical Coding (CPT, ICD-10)', icon: '🔢' },
    { name: 'Insurance Verification', icon: '✓' },
    { name: 'Denial Management', icon: '🔄' },
    { name: 'Payment Posting', icon: '💵' },
    { name: 'AR Management', icon: '📊' },
    { name: 'Patient Billing', icon: '👤' },
    { name: 'Reporting & Analytics', icon: '📈' }
  ];

  return (
    <>
      <Helmet>
        <title>Medical Billing Outsourcing Services USA | MDeRCM</title>
        <meta 
          name="description" 
          content="Leading medical billing outsourcing services. Reduce costs 30-50%, 98% claim accuracy, HIPAA-compliant. Expert billing for practices across USA."
        />
        <meta name="keywords" content="medical billing outsourcing, medical billing outsourcing services, medical billing outsourcing companies, medical billing and coding outsourcing, outsource medical billing" />
        <link rel="canonical" href="https://mdercm.com/medical-billing-outsourcing" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Medical Billing Outsourcing Services | MDeRCM" />
        <meta property="og:description" content="Expert medical billing outsourcing - Reduce costs 30-50%, improve accuracy 98%. Trusted by 500+ providers." />
        <meta property="og:url" content="https://mdercm.com/medical-billing-outsourcing" />
        <meta property="og:type" content="website" />
        
        {/* Schema Markup */}
        <script type="application/ld+json">
          {JSON.stringify(pageSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <main style={{ fontFamily: "'Poppins', sans-serif" }}>
        {/* Hero Section */}
        <section style={{
          background: 'linear-gradient(135deg, #003545 0%, #001F2B 100%)',
          padding: '120px 5% 80px',
          color: '#ffffff',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 style={{
                fontSize: 'clamp(2.2rem, 5vw, 3.5rem)',
                fontWeight: '800',
                marginBottom: '25px',
                lineHeight: '1.2'
              }}>
                Medical Billing Outsourcing Services
              </h1>
              <h2 style={{
                fontSize: 'clamp(1.3rem, 3vw, 2rem)',
                fontWeight: '600',
                marginBottom: '20px',
                color: '#F26522'
              }}>
                Reduce Costs 30-50% | 98% Claim Accuracy | HIPAA Compliant
              </h2>
              <p style={{
                fontSize: '1.2rem',
                lineHeight: '1.8',
                maxWidth: '800px',
                marginBottom: '35px',
                color: '#e0e0e0'
              }}>
                Partner with leading <strong>medical billing outsourcing companies</strong> to streamline your revenue cycle, reduce overhead costs, and focus on what matters most - patient care.
              </p>
              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                <Link to="/free-trial" style={{
                  display: 'inline-block',
                  padding: '15px 35px',
                  backgroundColor: '#F26522',
                  color: '#ffffff',
                  borderRadius: '30px',
                  textDecoration: 'none',
                  fontWeight: '700',
                  fontSize: '1.1rem',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d3541c'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#F26522'}
                >
                  Get Free Consultation
                </Link>
                <Link to="/why" style={{
                  display: 'inline-block',
                  padding: '15px 35px',
                  backgroundColor: 'transparent',
                  color: '#ffffff',
                  border: '2px solid #ffffff',
                  borderRadius: '30px',
                  textDecoration: 'none',
                  fontWeight: '700',
                  fontSize: '1.1rem',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#ffffff';
                  e.currentTarget.style.color = '#001F2B';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#ffffff';
                }}
                >
                  Why MDeRCM
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Stats Section */}
        <section style={{
          padding: '60px 5%',
          backgroundColor: '#f8f9fa'
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '30px',
              textAlign: 'center'
            }}>
              {[
                { number: '500+', label: 'Healthcare Providers', icon: '🏥' },
                { number: '30-50%', label: 'Cost Reduction', icon: '💰' },
                { number: '98%', label: 'Claim Accuracy', icon: '✅' },
                { number: '15-20', label: 'Days Payment Cycle', icon: '⚡' }
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  style={{
                    padding: '30px 20px',
                    backgroundColor: '#ffffff',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                  }}
                >
                  <div style={{ fontSize: '3rem', marginBottom: '10px' }}>{stat.icon}</div>
                  <div style={{ fontSize: '2.5rem', fontWeight: '800', color: '#F26522', marginBottom: '5px' }}>
                    {stat.number}
                  </div>
                  <div style={{ fontSize: '1rem', color: '#666', fontWeight: '500' }}>
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section style={{
          padding: '80px 5%',
          backgroundColor: '#ffffff'
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: 'clamp(2rem, 4vw, 2.8rem)',
              fontWeight: '700',
              textAlign: 'center',
              marginBottom: '20px',
              color: '#1A1A1A'
            }}>
              Why Choose Medical Billing Outsourcing?
            </h2>
            <p style={{
              fontSize: '1.1rem',
              textAlign: 'center',
              maxWidth: '800px',
              margin: '0 auto 50px',
              color: '#666',
              lineHeight: '1.7'
            }}>
              Discover how <strong>medical billing outsourcing services</strong> can transform your practice's revenue cycle and reduce administrative burden.
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: '30px'
            }}>
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  style={{
                    padding: '35px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '12px',
                    transition: 'all 0.3s ease',
                    border: '1px solid #e9ecef'
                  }}
                  whileHover={{
                    boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                    transform: 'translateY(-5px)'
                  }}
                >
                  <div style={{ fontSize: '3.5rem', marginBottom: '15px' }}>{benefit.icon}</div>
                  <h3 style={{ fontSize: '1.3rem', fontWeight: '700', marginBottom: '12px', color: '#0077b6' }}>
                    {benefit.title}
                  </h3>
                  <p style={{ fontSize: '1rem', color: '#555', lineHeight: '1.7' }}>
                    {benefit.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Services Included */}
        <section style={{
          padding: '80px 5%',
          backgroundColor: '#f8f9fa'
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: 'clamp(2rem, 4vw, 2.8rem)',
              fontWeight: '700',
              textAlign: 'center',
              marginBottom: '20px',
              color: '#1A1A1A'
            }}>
              Complete Medical Billing and Coding Outsourcing
            </h2>
            <p style={{
              fontSize: '1.1rem',
              textAlign: 'center',
              maxWidth: '800px',
              margin: '0 auto 50px',
              color: '#666'
            }}>
              Our comprehensive outsourcing services cover every aspect of your revenue cycle management.
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '25px'
            }}>
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.05 }}
                  viewport={{ once: true }}
                  style={{
                    padding: '25px',
                    backgroundColor: '#ffffff',
                    borderRadius: '10px',
                    textAlign: 'center',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                    border: '2px solid transparent',
                    transition: 'all 0.3s ease'
                  }}
                  whileHover={{
                    borderColor: '#F26522',
                    boxShadow: '0 8px 20px rgba(242,101,34,0.2)'
                  }}
                >
                  <div style={{ fontSize: '2.5rem', marginBottom: '10px' }}>{service.icon}</div>
                  <h4 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#333' }}>
                    {service.name}
                  </h4>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section style={{
          padding: '80px 5%',
          backgroundColor: '#ffffff'
        }} itemScope itemType="https://schema.org/FAQPage">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: 'clamp(2rem, 4vw, 2.8rem)',
              fontWeight: '700',
              textAlign: 'center',
              marginBottom: '50px',
              color: '#1A1A1A'
            }}>
              Frequently Asked Questions
            </h2>

            {faqSchema.mainEntity.map((faq, index) => (
              <div key={index} style={{
                marginBottom: '30px',
                padding: '30px',
                backgroundColor: '#f8f9fa',
                borderRadius: '12px',
                borderLeft: '4px solid #F26522'
              }} itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                <h3 style={{
                  fontSize: '1.3rem',
                  fontWeight: '600',
                  marginBottom: '15px',
                  color: '#0077b6'
                }} itemProp="name">
                  {faq.name}
                </h3>
                <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                  <p style={{
                    fontSize: '1rem',
                    lineHeight: '1.8',
                    color: '#555'
                  }} itemProp="text">
                    {faq.acceptedAnswer.text}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section style={{
          padding: '80px 5%',
          background: 'linear-gradient(135deg, #003545 0%, #001F2B 100%)',
          color: '#ffffff',
          textAlign: 'center'
        }}>
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: 'clamp(2rem, 4vw, 2.8rem)',
              fontWeight: '700',
              marginBottom: '20px'
            }}>
              Ready to Reduce Costs and Improve Collections?
            </h2>
            <p style={{
              fontSize: '1.2rem',
              marginBottom: '35px',
              lineHeight: '1.8',
              color: '#e0e0e0'
            }}>
              Join 500+ healthcare providers who trust MDeRCM for <strong>medical billing outsourcing services</strong>. Get started with a free consultation today!
            </p>
            <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/free-trial" style={{
                display: 'inline-block',
                padding: '18px 40px',
                backgroundColor: '#F26522',
                color: '#ffffff',
                borderRadius: '30px',
                textDecoration: 'none',
                fontWeight: '700',
                fontSize: '1.1rem',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d3541c'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#F26522'}
              >
                Start Free Trial
              </Link>
              <a href="tel:+15103566069" style={{
                display: 'inline-block',
                padding: '18px 40px',
                backgroundColor: 'transparent',
                color: '#ffffff',
                border: '2px solid #ffffff',
                borderRadius: '30px',
                textDecoration: 'none',
                fontWeight: '700',
                fontSize: '1.1rem',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#ffffff';
                e.currentTarget.style.color = '#001F2B';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#ffffff';
              }}
              >
                📞 Call +1-510-356-6069
              </a>
            </div>
          </div>
        </section>

        {/* Related Services */}
        <section style={{
          padding: '60px 5%',
          backgroundColor: '#f8f9fa'
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h3 style={{
              fontSize: '1.8rem',
              fontWeight: '700',
              textAlign: 'center',
              marginBottom: '40px',
              color: '#1A1A1A'
            }}>
              Related Services
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '25px'
            }}>
              {[
                { title: 'Hospital RCM', link: '/hospital-revenue-cycle-management', icon: '🏥' },
                { title: 'RCM Consulting', link: '/revenue-cycle-consulting', icon: '💼' },
                { title: 'AI Healthcare Solutions', link: '/healthcare/ai', icon: '🤖' },
                { title: 'Denial Management', link: '/healthcare/ai/denial-management', icon: '🔄' }
              ].map((item, index) => (
                <Link key={index} to={item.link} style={{
                  padding: '30px',
                  backgroundColor: '#ffffff',
                  borderRadius: '12px',
                  textAlign: 'center',
                  textDecoration: 'none',
                  color: '#333',
                  border: '2px solid #e9ecef',
                  transition: 'all 0.3s ease',
                  display: 'block'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#F26522';
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e9ecef';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                >
                  <div style={{ fontSize: '3rem', marginBottom: '15px' }}>{item.icon}</div>
                  <h4 style={{ fontSize: '1.2rem', fontWeight: '600', color: '#0077b6' }}>
                    {item.title}
                  </h4>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default MedicalBillingOutsourcing;